import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, ChevronRight, CalendarDays } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useVertical } from '../../context/VerticalContext';
import { AppointmentDetailsModal, type AppointmentLite } from './AppointmentDetailsModal';
import { APPOINTMENT_TYPES } from './appointmentTypes';
import { reportError } from '../../lib/errors';

interface Props {
    onBookSlot: () => void;
    refreshKey?: number;
}

/**
 * Appointments panel — one of the two lead columns on the dashboard.
 * Shows today's slots then the next 7 days in a single scroll (no card flip),
 * grouped by day. Links out to the full Schedule page. Tapping a row opens the
 * appointment details (where it can be converted to a diagnostic/job).
 */
export const AppointmentsCard = ({ onBookSlot, refreshKey = 0 }: Props) => {
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();
    const [rows, setRows] = useState<AppointmentLite[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<AppointmentLite | null>(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!activeOrgId) return;

        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 8);
        end.setHours(23, 59, 59);

        const fetch = async () => {
            setLoading(true);
            const { data, error: fetchError } = await supabase.rpc('fn_get_appointments_range', {
                p_org_id: activeOrgId,
                p_start_date: start.toISOString(),
                p_end_date: end.toISOString(),
            });
            if (fetchError) reportError(fetchError, 'appointments-card.get-appointments-range');
            // 'completed' belongs here too. The trigger that closes an
            // appointment when its job finishes was doing its job, but this card
            // still listed the booking — so a car that had been worked on and
            // collected sat on the dashboard looking like it was still due in.
            // Perfectly normal for a job to finish before the slot: the car came
            // in early.
            const active = ((data as AppointmentLite[]) ?? []).filter(
                (r) => r.status !== 'cancelled' && r.status !== 'no_show' && r.status !== 'completed',
            );
            active.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
            setRows(active);
            setLoading(false);
        };

        fetch().catch((err) => {
            console.error('Appointments fetch failed', err);
            setLoading(false);
        });
    }, [activeOrgId, refreshKey, tick]);

    const todayKey = new Date().toISOString().slice(0, 10);

    // Group by day
    const byDay = new Map<string, AppointmentLite[]>();
    for (const a of rows) {
        const key = new Date(a.start_time).toISOString().slice(0, 10);
        if (!byDay.has(key)) byDay.set(key, []);
        byDay.get(key)!.push(a);
    }

    return (
        <section className="fd-card flex flex-col h-full min-h-[22rem]" aria-labelledby="dash-appts-title">
            <header className="fd-card-header">
                <h2 id="dash-appts-title" className="fd-card-title">
                    <Calendar aria-hidden="true" />
                    <span className="truncate">Appointments</span>
                    {!loading && <span className="fd-count" aria-label={`${rows.length} booked`}>{rows.length}</span>}
                </h2>
                <button onClick={onBookSlot} className="fd-btn fd-btn-steel fd-btn-sm shrink-0">
                    <Plus aria-hidden="true" />
                    Book
                </button>
            </header>

            {loading ? (
                <div className="flex-1 grid place-items-center">
                    <span className="text-xs text-fg-4 font-mono">—</span>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-5 py-8 gap-3">
                    <Calendar className="w-8 h-8 text-fg-4" strokeWidth={1.5} />
                    <p className="text-sm text-fg-2 max-w-xs">Nothing booked in the next week.</p>
                    <button onClick={onBookSlot} className="fd-btn fd-btn-steel">
                        <Plus aria-hidden="true" />
                        Book slot
                    </button>
                </div>
            ) : (
                <div className="fd-card-body fd-dash-scroll flex-1">
                    {Array.from(byDay.entries()).map(([date, list]) => {
                        const d = new Date(`${date}T00:00:00`);
                        const isToday = date === todayKey;
                        const label = isToday
                            ? 'Today'
                            : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                        return (
                            <div key={date}>
                                <p
                                    className="fd-row-group"
                                    style={isToday ? { color: 'var(--fd-text-bright)' } : undefined}
                                >
                                    {label} · {list.length}
                                </p>
                                <ul className="flex flex-col">
                                    {list.map((a) => (
                                        <AppointmentRow key={a.id} a={a} onTap={() => setSelected(a)} />
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="fd-card-footer">
                <button
                    onClick={() => navigate('/schedule')}
                    className="fd-btn fd-btn-ghost fd-btn-sm w-full"
                >
                    <CalendarDays aria-hidden="true" />
                    View full calendar
                </button>
            </div>

            <AppointmentDetailsModal
                isOpen={selected !== null}
                appointment={selected}
                onClose={() => setSelected(null)}
                onChanged={() => setTick((t) => t + 1)}
            />
        </section>
    );
};

const AppointmentRow = ({ a, onTap }: { a: AppointmentLite; onTap: () => void }) => {
    const time = new Date(a.start_time).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const typeMeta = a.appointment_type ? APPOINTMENT_TYPES.find((t) => t.value === a.appointment_type) : null;
    return (
        <li className="min-w-0">
            <button onClick={onTap} className="fd-row">
                <span className="font-mono text-fd-sm-tight font-medium text-fg-2 tabular-nums w-11 flex-shrink-0">
                    {time}
                </span>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-fg truncate">
                        {a.customer_name || 'Walk-in'}
                    </span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {a.vehicle_vrm && (
                            <span className="fd-plate">{a.vehicle_vrm}</span>
                        )}
                        {typeMeta && (
                            <span className="text-fd-3xs text-fg-3 font-display font-medium uppercase tracking-wider">
                                {typeMeta.label}
                            </span>
                        )}
                    </div>
                </div>
                <ChevronRight className="w-4 h-4 text-fg-4 flex-shrink-0" />
            </button>
        </li>
    );
};
